import { FC, HTMLAttributes } from 'react';
import { Col, ColProps, Row, RowProps } from 'antd';
import styled from 'styled-components';
import { colorBgContainer, token } from '@atlas-frontend/design-tokens';
import { BasicProps } from 'antd/es/layout/layout';
import { useResponsiveGlobalTokenValue } from '../useResponsiveGlobalTokenValue';

const HEADER_AND_FOOTER_HEIGHT_PX = 60;

type MainFC = FC<RowProps> & {
  Col: typeof MainCol;
  Header: typeof MainHeader;
  Footer: typeof MainFooter;
  Content: typeof MainContent;
};

export const Main: MainFC = (props) => <StyledMain {...props} wrap={false} />;

const StyledMain = styled(Row)`
  height: 100%;
  max-height: 100vh;
`;

interface MainColProps extends ColProps {
  isOpen?: boolean;
  border?: boolean;
  bg?: boolean;
}

export const MainCol: FC<MainColProps> = (props) => {
  const { isOpen, flex = 'auto', border = true, bg = false, ...rest } = props;
  return (
    <StyledMainCol
      {...rest}
      flex={flex}
      $isOpen={isOpen}
      $border={border}
      $bg={bg}
    />
  );
};

const StyledMainCol = styled(Col)<{
  $isOpen?: boolean;
  $border?: boolean;
  $bg?: boolean;
}>`
  display: ${({ $isOpen = true }) => ($isOpen ? 'flex' : 'none')};
  flex-direction: column;
  max-height: 100%;
  ${({ $border }) =>
    $border ? `border-left: 1px solid ${token.colorBorderSecondary}` : ''};
  ${({ $bg }) => ($bg ? `background: ${colorBgContainer}` : '')};
`;

interface MainEnderProps extends BasicProps {
  end: 'header' | 'footer';
  padding?: boolean;
}

const Ender: FC<MainEnderProps> = (props) => {
  const { end, padding = true, ...rest } = props;
  const paddingPx = useResponsiveGlobalTokenValue('padding');
  return (
    <StyledEnder {...rest} $end={end} $padding={padding ? paddingPx : 0} />
  );
};

const StyledEnder = styled.div<{
  $end: 'header' | 'footer';
  $padding: number;
}>`
  background: ${colorBgContainer};
  height: ${HEADER_AND_FOOTER_HEIGHT_PX}px;
  padding-inline: ${({ $padding = 0 }) => $padding}px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  z-index: 2;
  transition: none;
  border-${({ $end }) => ($end === 'header' ? 'bottom' : 'top')}: 1px solid ${
    token.colorBorderSecondary
  };

  > h1,
  > h2,
  > h3,
  > h4,
  > h5,
  > h6 {
    margin: 0;
    text-align: center;
    flex: auto;
  }
`;

export const MainHeader: FC<Omit<MainEnderProps, 'end'>> = (props) => (
  <Ender {...props} end="header" />
);

export const MainFooter: FC<Omit<MainEnderProps, 'end'>> = (props) => (
  <Ender {...props} end="footer" />
);

export interface MainContentProps extends HTMLAttributes<HTMLDivElement> {
  padding?: boolean;
  scrollable?: boolean;
}

export const MainContent: FC<MainContentProps> = (props) => {
  const { padding = true, scrollable = true, ...rest } = props;
  const paddingPx = useResponsiveGlobalTokenValue('padding');
  return (
    <StyledContent
      {...rest}
      $padding={padding ? paddingPx : 0}
      $scrollable={scrollable}
    />
  );
};

export const StyledContent = styled.div<{
  $padding?: number;
  $scrollable: boolean;
}>`
  flex: auto;
  min-height: 0;
  padding: ${({ $padding = 0 }) => $padding}px;
  display: flex;
  flex-direction: column;
  ${({ $scrollable }) => ($scrollable ? 'overflow: auto;' : '')}
`;

Main.Col = MainCol;
Main.Header = MainHeader;
Main.Footer = MainFooter;
Main.Content = MainContent;
